import { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api/client';
import FileUploader from '../common/FileUploader';
import { UploadProgressList } from '../common/UploadProgress';

const CATEGORY_OPTIONS = [
  { value: 'music', label: 'Música' },
  { value: 'program', label: 'Programa' },
  { value: 'advertising', label: 'Publicidad' },
  { value: 'jingle', label: 'Jingle' },
  { value: 'promo', label: 'Promo' },
  { value: 'fx', label: 'FX / Sweep' },
  { value: 'playlist', label: 'Playlist' },
  { value: 'special', label: 'Especial' },
  { value: 'fallback', label: 'Fallback' },
];

export default function MediaUpload() {
  const navigate = useNavigate();

  const [uploads, setUploads] = useState([]);
  const [category, setCategory] = useState('music');
  const [folderId, setFolderId] = useState('');
  const [folders, setFolders] = useState([]);
  const [duplicate, setDuplicate] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchFolders = useCallback(async () => {
    try {
      const { data } = await api.get('/api/music/folders');
      setFolders(data);
    } catch (err) {
      console.error('[MediaUpload] Failed to fetch folders:', err);
    }
  }, []);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  const handleUpload = async (file, uploadId, onProgress, extra = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category);
    if (folderId) fd.append('folder_id', folderId);
    if (extra.overwrite) fd.append('overwrite', '1');
    if (extra.rename) fd.append('rename', extra.rename);

    const response = await api.post('/api/media/upload', fd, {
      onUploadProgress: (prog) => {
        const percent = (prog.event.loaded / prog.event.total) * 100;
        onProgress(percent);
      },
    });
    return response;
  };

  const handleDuplicate = (uploadId, file, data) => {
    setDuplicate({ uploadId, file, data });
    setRenameValue(data?.existing_name ? `copia de ${data.existing_name}` : '');
  };

  const doDuplicateAction = async (action) => {
    if (!duplicate || busy) return;
    setBusy(true);
    const { uploadId, file } = duplicate;
    try {
      const extra = {};
      if (action === 'overwrite') {
        extra.overwrite = true;
      } else if (action === 'rename') {
        const name = renameValue.trim();
        if (!name) return;
        extra.rename = name;
      }
      setUploads((prev) => [...prev, { id: uploadId, file, progress: 0, status: 'uploading' }]);
      await handleUpload(file, uploadId, () => {}, extra);
      setUploads((prev) => prev.map((u) => (u.id === uploadId ? { ...u, progress: 100, status: 'ready' } : u)));
      setTimeout(() => setUploads((prev) => prev.filter((u) => u.id !== uploadId)), 2000);
    } catch (err) {
      console.error('[MediaUpload] Duplicate action failed:', err);
      setUploads((prev) => prev.map((u) => (u.id === uploadId ? { ...u, status: 'error' } : u)));
    } finally {
      setBusy(false);
      setDuplicate(null);
      setRenameValue('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">Subir Medio</h1>
        <Link to="/media" className="btn btn-secondary btn-sm">
          Volver a la Biblioteca
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1 space-y-4">
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Categoría</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="input">
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Carpeta</label>
            <select value={folderId} onChange={(e) => {
              const fid = e.target.value;
              setFolderId(fid);
              const f = folders.find((x) => x.id === fid);
              if (f && f.category) setCategory(f.category);
            }} className="input">
              <option value="">Sin Carpeta</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="lg:col-span-2">
          <FileUploader
            onUpload={handleUpload}
            onDuplicate={handleDuplicate}
            accept="audio/*,.mp3,.wav,.aac,.flac,.ogg,.m4a,.wma"
            multiple={true}
            maxSizeMB={500}
            pollMediaStatus
            label="Arrastra y suelta los archivos aquí, o haz clic para explorar"
          />
        </div>
      </div>

      {uploads.length > 0 && (
        <div className="rounded-xl bg-dark-850 border border-dark-700 p-4">
          <h3 className="text-sm font-medium text-dark-300 mb-3">Progreso de Subida</h3>
          <UploadProgressList uploads={uploads} />
        </div>
      )}

      {/* Diálogo de archivo duplicado */}
      {duplicate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-xl bg-dark-800 p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-dark-100 mb-2">Archivo ya cargado</h3>
            <p className="text-sm text-dark-300 mb-1">
              <span className="text-dark-400">"</span>{duplicate.file.name}<span className="text-dark-400">"</span> ya está en el sistema como
            </p>
            <p className="text-sm font-medium text-primary-400 mb-4">"{duplicate.data?.existing_name || 'archivo existente'}"</p>

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => doDuplicateAction('overwrite')}
                disabled={busy}
                className="btn btn-primary w-full"
              >
                Sobrescribir el existente
              </button>

              <div>
                <button
                  type="button"
                  onClick={() => doDuplicateAction('rename')}
                  disabled={busy || !renameValue.trim()}
                  className="btn btn-secondary w-full"
                >
                  Subir con otro nombre
                </button>
                <input
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  className="input text-sm mt-2"
                  placeholder="Nuevo nombre del archivo..."
                />
              </div>

              <button
                type="button"
                onClick={() => setDuplicate(null)}
                disabled={busy}
                className="btn btn-outline w-full"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 border-t border-dark-700 pt-6">
        <Link to="/media" className="btn btn-secondary">
          Cancelar
        </Link>
      </div>
    </div>
  );
}
